import moment from 'moment';

import { Patient } from '../store/types/patient';
import { getAgeInMonths } from '../store/utils';
import { AORTA_CREATE_PATIENT, AORTA_UPDATE_PATIENT } from './constants';
import fetchWithRefresh from './fetchWithRefresh';
import { MHC } from '../store/types/medicalHistory';
import { Relation } from './fetchPatientDirByDocOid';
import { ONE_CHAR_GENDER } from '../pages/NewPatient';
import { getMe } from '../store/idbHandler';

export interface CreateUpdatePatientRequest {
    bloodgroup?: string;
    s?: string;
    email?: string;
    ln?: string;
    fln?: string;
    extras?: Extras;
    dob?: string;
    gen?: string;
    ps?: string;
    username?: string;
    mobile?: string;
    'health-ids'?: string[] | null;
}
export interface Extras {
    [k: string]: FormDataEntity[] | string | null | undefined | string[] | any;
}
export interface FormDataEntity {
    label: string;
    type: string;
    key: string;
    value: string;
}

export interface PushDataRequest {
    patient: BEPatient;
    created_at?: string;
    visitid?: string;
    sendwelcome: boolean;
    formData?: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    edited_from?: string | string[];
    link?: string[];
    patientMedicalHistory?: MHC;
    refer_id?: string;
    refer_doc_id?: string;
    relations?: Relation[];
}
export interface BEPatient {
    id?: string;
    profile: Profile;
    refer_id?: string;
    referredBy?: string;
}
export interface Profile {
    personal: Personal;
}
export interface Personal {
    name: string;
    phone?: Phone;
    age?: Age;
    gender: string;
}
export interface Phone {
    c?: string;
    n?: string;
}
export interface Age {
    dob?: string;
    ageInMonths?: number;
}

export interface UpsertPatientResponse {
    code?: string;
    message?: string;
    oid: string;
    mobile: string;
    email: string;
    username: string;
    ln: string;
    fln: string;
    abha: string;
    gen: string;
    dob: string;
    bloodgroup: string;
    c_ate?: number;
    u_ate?: number;
    extras: Extras;
    ps: string;
    uuid?: string;
    _custom_uuid?: string;
    'health-ids'?: string[];
    uat?: string;
    at: string;
    deact?: boolean;
}

export interface FormDataEntity {
    label: string;
    type: string;
    key: string;
    value: string;
}

export interface PushPatientResponse {
    status: string;
    remark: string;
    response?: {
        pid?: string;
        ptProfile?: PtProfile;
    };
}

export interface PtProfile {
    id: string;
    uuid?: string;
    profile: PtProfileProfile;
    formData?: {
        label: string;
        type: 'number' | 'string' | 'select';
        key: string;
        value: string | number;
    }[];
    archived: boolean;
    link?: string[];
    created_at: string;
    updated_at: string;
    docids?: string[];
}
export interface PtProfileProfile {
    personal: PtProfilePersonal;
}
export interface PtProfilePersonal {
    name: string;
    age: PtProfileAge;
    phone?: Phone;
    gender: string;
    bloodgroup: string;
    onApp: boolean;
}
export interface PtProfileAge {
    dob: string;
}
export interface Phone {
    c?: string;
    n?: string;
}

const SALUTATION_KEY = 'salutation';

export default (patient: Patient): Promise<UpsertPatientResponse> => {
    const ageInMonths = patient.personal.age && getAgeInMonths(patient.personal.age);

    let dob = patient.personal.age?.dob;
    if (!dob && ageInMonths) {
        const date = moment().subtract(ageInMonths, 'months');
        dob = date.format('YYYY-MM-DD');
    }

    const ptFormData = patient.formData?.map((data) => {
        if (data.key === SALUTATION_KEY) {
            return {
                ...data,
                value: data.value ? `${data.value}`.replace('.', '') : data.value,
            };
        }
        return data;
    });

    const s = ptFormData?.find((data) => data.key === SALUTATION_KEY)?.value?.toString();
    const uhid = ptFormData?.find((data) => data.key === 'uhid')?.value?.toString();

    const reqBody: CreateUpdatePatientRequest = {
        fln: [patient.personal.name.f, patient.personal.name.l].filter(Boolean).join(' '),
        mobile: [patient.personal.phone?.c, patient.personal.phone?.n].filter(Boolean).join(''),
        gen: ONE_CHAR_GENDER[patient.personal.gender.toLowerCase()] || undefined,
        dob,
        s,
        username: uhid,
        extras: {
            refer_id: patient.refer_id,
            referredBy: patient?.referredBy,
            formData: ptFormData,
            link: patient.link,
            refer_doc_id: patient.refer_doc_id,
            relations: patient.relations,
            _db: getMe()?.user['b-id'],
        },
    };

    return fetchWithRefresh(patient.id ? AORTA_UPDATE_PATIENT(patient.id) : AORTA_CREATE_PATIENT, {
        method: patient.id ? 'PATCH' : 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(reqBody),
    }).then((res) => {
        return res
            .json()
            .then((t) => {
                if (res.ok) {
                    return t;
                } else {
                    throw new Error(t.code + t.message);
                }
            })
            .catch((err) => {
                throw new Error(err);
            });
    });
};
